<template>
  <aside class="sidebar">
    <div class="nav-scroll">
      <DashboardSearchBox
        v-if="showSearch"
        class="sidebar-search"
        :model-value="searchQuery"
        :white-on-mobile="true"
        @update:model-value="$emit('search-change', $event)"
        @submit="$emit('search-submit')"
      />

      <!-- ── Modo Almoxarifado ── -->
      <template v-if="warehouseMode">
        <button v-if="!isAnalyst" class="create-btn create-btn--warehouse" type="button" @click="$emit('open-create-popup')">
          <span class="create-icon">+</span>
          Criar
        </button>
        <div class="identity-block warehouse-identity-block">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;opacity:.7"><path d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"/></svg>
          Almoxarifado
        </div>
        <div class="nav-group" style="margin-top:4px">
          <div class="nav-group-header"><span>Estoque</span></div>
          <nav class="nav-links">
            <button class="nav-link" :class="{ active: isWarehouseTab('estoque') }" type="button" @click="$emit('warehouse-stock')">
              Inventário
            </button>
            <button class="nav-link" :class="{ active: isWarehouseTab('solicitacoes') }" type="button" @click="$emit('warehouse-requests')">
              Solicitações
            </button>
            <button class="nav-link" :class="{ active: isWarehouseTab('planejamento') }" type="button" @click="$emit('warehouse-planejamento')">
              Planejamento
            </button>
            <button class="nav-link" :class="{ active: activePath.startsWith('/almoxarifado/avarias') }" type="button" @click="$emit('warehouse-avarias')">
              Avarias
            </button>
          </nav>
        </div>
        <div class="nav-group">
          <div class="nav-group-header"><span>Operações</span></div>
          <nav class="nav-links">
            <button class="nav-link" :class="{ active: activePath.startsWith('/almoxarifado/importar-nf') }" type="button" @click="$emit('warehouse-nfe-import')">
              Importar NF
            </button>
            <button class="nav-link" :class="{ active: activePath.startsWith('/almoxarifado/solicitar-item') }" type="button" @click="$emit('warehouse-request-item')">
              Solicitar Item
            </button>
          </nav>
        </div>
        <div class="nav-group">
          <div class="nav-group-header"><span>Frota</span></div>
          <nav class="nav-links">
            <button class="nav-link" :class="{ active: activePath.startsWith('/maquinario-pesado') }" type="button" @click="$emit('heavy-machinery')">
              Maquinário Pesado
            </button>
          </nav>
        </div>
      </template>

      <!-- ── Modo Escritório ── -->
      <template v-if="officeMode">
        <button v-if="!isAnalyst" class="create-btn create-btn--office" type="button" @click="$emit('open-create-popup')">
          <span class="create-icon">+</span>
          Criar
        </button>
        <div class="identity-block office-identity-block">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;opacity:.7"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>
          Escritório
        </div>
        <div class="nav-group" style="margin-top:4px">
          <div class="nav-group-header"><span>Cadastros</span></div>
          <nav class="nav-links">
            <button class="nav-link" :class="{ active: officeTab === 'fornecedores' }" type="button" @click="$emit('office-tab', 'fornecedores')">
              Fornecedores
            </button>
          </nav>
        </div>
        <div class="nav-group">
          <div class="nav-group-header"><span>Compras</span></div>
          <nav class="nav-links">
            <button class="nav-link" :class="{ active: officeTab === 'rc' }" type="button" @click="$emit('office-tab', 'rc')">
              Req. de Compra
            </button>
            <button class="nav-link" :class="{ active: officeTab === 'oc' }" type="button" @click="$emit('office-tab', 'oc')">
              Ordens de Compra
            </button>
          </nav>
        </div>
        <div class="nav-group">
          <div class="nav-group-header"><span>Financeiro</span></div>
          <nav class="nav-links">
            <button class="nav-link" :class="{ active: officeTab === 'nf' }" type="button" @click="$emit('office-tab', 'nf')">
              Notas Fiscais
            </button>
            <button class="nav-link" :class="{ active: officeTab === 'cc' }" type="button" @click="$emit('office-tab', 'cc')">
              Centro de Custo
            </button>
          </nav>
        </div>
        <div class="nav-group">
          <div class="nav-group-header"><span>Treinamento</span></div>
          <nav class="nav-links">
            <button class="nav-link" :class="{ active: officeTab === 'questionarios' }" type="button" @click="$emit('office-tab', 'questionarios')">
              Questionários
            </button>
          </nav>
        </div>
        <div class="nav-group">
          <div class="nav-group-header"><span>Configurações</span></div>
          <nav class="nav-links">
            <button class="nav-link" :class="{ active: officeTab === 'gerencia' }" type="button" @click="$emit('office-tab', 'gerencia')">
              Gerenciar Escritório
            </button>
          </nav>
        </div>
      </template>

      <!-- ── Modo Manutenção ── -->
      <template v-if="maintenanceMode">
        <button class="create-btn create-btn--maintenance" type="button" @click="$emit('open-create-popup')">
          <span class="create-icon">+</span>
          Criar
        </button>
        <div class="identity-block maint-identity-block">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;opacity:.7"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>
          Manutenção
        </div>
        <div class="nav-group" style="margin-top:4px">
          <div class="nav-group-header"><span>Geral</span></div>
          <nav class="nav-links">
            <button class="nav-link" :class="{ active: activePath === '/manutencao' }" type="button" @click="$emit('maintenance')">
              Visão Geral
            </button>
            <button v-if="canAssignMaintenance" class="nav-link" :class="{ active: activePath === '/manutencao/ordens-servico' }" type="button" @click="$emit('maintenance-service-orders')">
              Gerenciar OS
            </button>
            <button v-if="canAssignMaintenance" class="nav-link" :class="{ active: activePath === '/manutencao/consultar-os' }" type="button" @click="$emit('maintenance-os-consulta')">
              Consultar OS
            </button>
            <button class="nav-link nav-link--manuais" :class="{ active: activePath === '/manutencao/manuais' }" type="button" @click="$emit('maintenance-manuals')">
              Manuais
              <span class="nav-beta-badge">Beta</span>
            </button>
          </nav>
        </div>
      </template>

      <!-- ── Modo Portaria ── -->
      <template v-if="portariaMode">
        <div class="identity-block portaria-identity-block">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;opacity:.7"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><path d="M9 22V12h6v10"/></svg>
          Portaria
        </div>
        <div class="nav-group" style="margin-top:4px">
          <div class="nav-group-header"><span>Geral</span></div>
          <nav class="nav-links">
            <button class="nav-link" :class="{ active: activePath === '/portaria' }" type="button" @click="$emit('go-portaria')">
              Visão Geral
            </button>
          </nav>
        </div>
      </template>

      <!-- ── Normal (construção / admin) ── -->
      <template v-else-if="!warehouseMode && !officeMode && !maintenanceMode && !portariaMode">
        <button v-if="!isAnalyst" class="create-btn" type="button" @click="$emit('open-create-popup')">
          <span class="create-icon">+</span>
          Criar
        </button>

        <div class="identity-block">{{ systemName }}</div>
        <div v-if="projectName" class="project-label">
          <svg class="project-icon" width="12" height="12" viewBox="0 0 12 12" fill="none">
            <rect x="1" y="1" width="10" height="10" rx="2" stroke="currentColor" stroke-width="1.4"/>
            <path d="M4 4h4M4 6h4M4 8h2" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
          </svg>
          <span class="project-label-text">{{ projectName }}</span>
        </div>

        <div v-if="panelTitle && !projectName" class="panel-title-label">{{ panelTitle }}</div>

        <!-- Nav: Analista (visualização completa, sem edição) -->
        <div v-if="isAnalyst" class="nav-group">
        <div class="nav-group-header">
          <span>{{ $t('nav.dashboard') }}</span>
        </div>
        <nav class="nav-links">
          <button class="nav-link" :class="{ active: activePath === '/visao-geral' || activePath === '/home' }" type="button" @click="$emit('overview')">
            {{ $t('nav.overview') }}
          </button>
          <button class="nav-link" :class="{ active: activePath === '/consulta-documentos' }" type="button" @click="$emit('documents')">
            {{ $t('nav.viewDocuments') }}
          </button>
          <button class="nav-link" :class="{ active: activePath === '/gerenciar-projetos' }" type="button" @click="$emit('projects')">
            {{ $t('nav.viewProjects') }}
          </button>
          <button class="nav-link" :class="{ active: activePath === '/gerenciar-usuarios' }" type="button" @click="$emit('users')">
            {{ $t('nav.viewUsers') }}
          </button>
          <button class="nav-link" :class="{ active: activePath.startsWith('/almoxarifado') }" type="button" @click="$emit('warehouse-stock')">
            {{ $t('nav.viewWarehouse') }}
          </button>
          <button class="nav-link" :class="{ active: activePath.startsWith('/maquinario-pesado') }" type="button" @click="$emit('heavy-machinery')">
            {{ $t('nav.viewMachinery') }}
          </button>
        </nav>
      </div>

      <!-- Nav: Admin (hub central — acesso a todos os modos) -->
      <div v-if="isAdmin && !isAnalyst && adminMode" class="admin-hub">

        <!-- ─ Administração ─ -->
        <div class="nav-group" style="margin-top:4px">
          <div class="nav-group-header"><span>Administração</span></div>
          <nav class="nav-links">
            <button class="nav-link" :class="{ active: activePath === '/visao-geral' || activePath === '/home' }" type="button" @click="$emit('overview')">
              Visão Geral
            </button>
            <button class="nav-link" :class="{ active: activePath === '/gerenciar-projetos' }" type="button" @click="$emit('projects')">
              Gerenciar Projetos
            </button>
            <button class="nav-link" :class="{ active: activePath === '/gerenciar-usuarios' }" type="button" @click="$emit('users')">
              Usuários
            </button>
            <button class="nav-link" :class="{ active: activePath === '/consulta-documentos' }" type="button" @click="$emit('documents')">
              Documentos
            </button>
            <button class="nav-link" :class="{ active: activePath.startsWith('/chamados') }" type="button" @click="$emit('tickets')">
              Chamados
              <span v-if="ticketPendingCount > 0" class="ticket-badge">{{ ticketBadgeText }}</span>
            </button>
            <button v-if="isMasterAdmin" class="nav-link" :class="{ active: activePath === '/gerenciar-sistema' }" type="button" @click="$emit('system')">
              Configurar Sistema
            </button>
          </nav>
        </div>

        <!-- ─ Construção ─ -->
        <div v-if="canManageConstruction" class="nav-group">
          <div class="nav-group-header nav-group-header--mode">
            <span>Construção</span>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" style="opacity:.5"><polyline points="9 18 15 12 9 6"/></svg>
          </div>
          <nav class="nav-links">
            <button class="nav-link" :class="{ active: activePath === '/meus-projetos' }" type="button" @click="$emit('my-projects')">
              Meus Projetos
            </button>
            <button class="nav-link" :class="{ active: activePath === '/meus-relatorios' }" type="button" @click="$emit('filter', '')">
              Relatórios
            </button>
            <button class="nav-link" :class="{ active: activePath === '/desenhos' }" type="button" @click="$emit('drawings')">
              Desenhos
            </button>
          </nav>
        </div>

        <!-- ─ Manutenção ─ -->
        <div v-if="canManageMaintenance" class="nav-group">
          <div class="nav-group-header nav-group-header--mode">
            <span>Manutenção</span>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" style="opacity:.5"><polyline points="9 18 15 12 9 6"/></svg>
          </div>
          <nav class="nav-links">
            <button class="nav-link" :class="{ active: activePath === '/manutencao' }" type="button" @click="$emit('maintenance')">
              Visão Geral
            </button>
          </nav>
        </div>

        <!-- ─ Almoxarifado ─ -->
        <div v-if="canManageWarehouse" class="nav-group">
          <div class="nav-group-header nav-group-header--mode">
            <span>Almoxarifado</span>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" style="opacity:.5"><polyline points="9 18 15 12 9 6"/></svg>
          </div>
          <nav class="nav-links">
            <button class="nav-link" :class="{ active: activePath.startsWith('/almoxarifado') && !activePath.includes('avarias') && !activePath.includes('importar') }" type="button" @click="$emit('warehouse-stock')">
              Inventário
            </button>
            <button class="nav-link" :class="{ active: activePath.startsWith('/almoxarifado/avarias') }" type="button" @click="$emit('warehouse-avarias')">
              Avarias
            </button>
            <button class="nav-link" :class="{ active: activePath.startsWith('/almoxarifado/importar-nf') }" type="button" @click="$emit('warehouse-nfe-import')">
              Importar NF
            </button>
            <button class="nav-link" :class="{ active: activePath.startsWith('/maquinario-pesado') }" type="button" @click="$emit('heavy-machinery')">
              Maquinário Pesado
            </button>
          </nav>
        </div>

        <!-- ─ Escritório ─ -->
        <div v-if="canManageOffice" class="nav-group">
          <div class="nav-group-header nav-group-header--mode">
            <span>Escritório</span>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" style="opacity:.5"><polyline points="9 18 15 12 9 6"/></svg>
          </div>
          <nav class="nav-links">
            <button class="nav-link" type="button" @click="$emit('office-tab', 'fornecedores')">
              Fornecedores
            </button>
            <button class="nav-link" type="button" @click="$emit('office-tab', 'rc')">
              Req. de Compra
            </button>
            <button class="nav-link" type="button" @click="$emit('office-tab', 'oc')">
              Ordens de Compra
            </button>
            <button class="nav-link" type="button" @click="$emit('office-tab', 'nf')">
              Notas Fiscais
            </button>
            <button class="nav-link" type="button" @click="$emit('office-tab', 'cc')">
              Centro de Custo
            </button>
            <button class="nav-link" type="button" @click="$emit('office-tab', 'questionarios')">
              Questionários
            </button>
            <button class="nav-link" type="button" @click="$emit('office-tab', 'gerencia')">
              Gerenciar Escritório
            </button>
          </nav>
        </div>

      </div><!-- /admin-hub -->

      <div v-if="!adminMode && !isAnalyst" class="nav-group" style="margin-top: 4px;">
        <div class="nav-group-header">
          <span>{{ $t('nav.projects') }}</span>
        </div>
        <nav class="nav-links">
          <!--<button
            class="nav-link"
            :class="{ active: activePath === '/minhas-tarefas' }"
            type="button"
            @click="$emit('my-tasks')"
          >
            {{ $t('nav.myTasks') }}
          </button>-->
          <button
            class="nav-link"
            :class="{ active: activePath === '/meus-projetos' || activePath === '/home' }"
            type="button"
            @click="$emit('my-projects')"
          >
            {{ $t('nav.myProjects') }}
          </button>
          <button
            class="nav-link"
            :class="{ active: activePath === '/meus-relatorios' }"
            type="button"
            @click="$emit('filter', '')"
          >
            {{ $t('nav.reports') }}
          </button>
          <button
            class="nav-link"
            :class="{ active: activePath === '/desenhos' }"
            type="button"
            @click="$emit('drawings')"
          >
            {{ $t('nav.drawings') }}
          </button>
        </nav>
      </div>

      <button
        v-if="canRequestWarehouse && !adminMode && !isAnalyst"
        class="nav-group-header nav-group-link"
        :class="{ active: activePath.startsWith('/almoxarifado/solicitar-item') }"
        type="button"
        @click="$emit('warehouse-request-item')"
      >
        <span>{{ $t('nav.requestItem') }}</span>
      </button>

      <button
        v-if="!adminMode && !isAnalyst && showTickets"
        class="nav-group-header nav-group-link"
        :class="{ active: activePath.startsWith('/chamados') }"
        type="button"
        @click="$emit('tickets')"
      >
        <span>{{ $t('nav.tickets') }}</span>
        <span v-if="ticketPendingCount > 0" class="ticket-badge">{{ ticketBadgeText }}</span>
      </button>
      </template><!-- /v-else normal -->
    </div><!-- /.nav-scroll -->

    <div class="mode-toggle">
      <!-- backdrop: closes menu on outside click -->
      <div v-if="showModeMenu" class="mode-menu-backdrop" @click="showModeMenu = false" />

      <div class="sidebar-user-name">{{ userName }}</div>
      <button class="msg-btn" type="button" @click="$emit('messenger')">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
        </svg>
        {{ $t('nav.messages') }}
        <span v-if="messengerUnread > 0" class="msg-badge">{{ messengerUnread > 9 ? '+9' : messengerUnread }}</span>
      </button>

      <!-- Mode picker popup -->
      <div v-if="showModeMenu && !isAnalyst" class="mode-menu">
        <div class="mode-menu-label">Selecionar sistema</div>
        <button
          v-if="canManageConstruction"
          class="mode-option"
          :class="{ active: !maintenanceMode && !adminMode && !warehouseMode && !officeMode && !portariaMode }"
          type="button"
          @click="setMode('construcao')"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
          Construção
          <svg v-if="!maintenanceMode && !adminMode && !warehouseMode && !officeMode && !portariaMode" class="mode-check" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
        </button>
        <button
          v-if="canManageMaintenance"
          class="mode-option mode-option--maint"
          :class="{ active: maintenanceMode }"
          type="button"
          @click="setMode('manutencao')"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>
          Manutenção
          <svg v-if="maintenanceMode" class="mode-check" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
        </button>
        <button
          v-if="canManageWarehouse"
          class="mode-option mode-option--warehouse"
          :class="{ active: warehouseMode }"
          type="button"
          @click="setMode('almoxarifado')"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0"><path d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"/></svg>
          Almoxarifado
          <svg v-if="warehouseMode" class="mode-check" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
        </button>
        <button
          v-if="canManageOffice"
          class="mode-option mode-option--office"
          :class="{ active: officeMode }"
          type="button"
          @click="setMode('escritorio')"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>
          Escritório
          <svg v-if="officeMode" class="mode-check" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
        </button>
        <button
          v-if="canManagePortaria"
          class="mode-option mode-option--portaria"
          :class="{ active: portariaMode }"
          type="button"
          @click="setMode('portaria')"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><path d="M9 22V12h6v10"/></svg>
          Portaria
          <svg v-if="portariaMode" class="mode-check" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
        </button>
        <button
          v-if="isAdmin"
          class="mode-option mode-option--admin"
          :class="{ active: !maintenanceMode && adminMode && !warehouseMode && !officeMode && !portariaMode }"
          type="button"
          @click="setMode('admin')"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          Administrador
          <svg v-if="!maintenanceMode && adminMode && !warehouseMode && !officeMode && !portariaMode" class="mode-check" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
        </button>
      </div>

      <!-- "Mudar sistema" button (hidden for analysts) -->
      <button
        v-if="!isAnalyst"
        class="change-system-btn"
        :class="{ 'change-system-btn--maint': maintenanceMode, 'change-system-btn--warehouse': warehouseMode, 'change-system-btn--office': officeMode, 'change-system-btn--portaria': portariaMode, 'change-system-btn--admin': adminMode && !maintenanceMode && !warehouseMode && !officeMode && !portariaMode, 'change-system-btn--open': showModeMenu }"
        type="button"
        @click="showModeMenu = !showModeMenu"
      >
        <svg v-if="maintenanceMode" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>
        <svg v-else-if="warehouseMode" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0"><path d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"/></svg>
        <svg v-else-if="officeMode" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>
        <svg v-else-if="portariaMode" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><path d="M9 22V12h6v10"/></svg>
        <svg v-else-if="adminMode" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
        <svg v-else width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
        {{ modeLabelText }}
        <svg class="chevron-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;margin-left:auto"><polyline points="18 15 12 9 6 15"/></svg>
      </button>

      <div v-if="isAnalyst" class="analyst-badge">
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        {{ $t('nav.analystMode') }}
      </div>

      <AppFooter class="sidebar-footer" />
    </div>
  </aside>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from 'vue'
import DashboardSearchBox from '@/components/dashboard/DashboardSearchBox.vue'
import AppFooter from '@/components/Footer.vue'

export default defineComponent({
  name: 'DashboardSidebar',
  components: {
    DashboardSearchBox,
    AppFooter
  },
  props: {
    userName: {
      type: String,
      required: true
    },
    systemName: {
      type: String,
      default: 'Obra Tycoon'
    },
    isAdmin: {
      type: Boolean,
      default: false
    },
    isMasterAdmin: {
      type: Boolean,
      default: false
    },
    activePath: {
      type: String,
      default: '/home'
    },
    isAnalyst: { type: Boolean, default: false },
    canManageWarehouse: { type: Boolean, default: false },
    canRequestWarehouse: { type: Boolean, default: false },
    hasSubordinates: {
      type: Boolean,
      default: false
    },
    showSearch: {
      type: Boolean,
      default: false
    },
    searchQuery: {
      type: String,
      default: ''
    },
    ticketPendingCount: {
      type: Number,
      default: 0
    },
    projectName: {
      type: String,
      default: ''
    },
    messengerUnread: {
      type: Number,
      default: 0
    },
    panelTitle: {
      type: String,
      default: ''
    },
    maintenanceEnabled: {
      type: Boolean,
      default: false
    },
    canManageConstruction: {
      type: Boolean,
      default: true
    },
    canManageMaintenance: {
      type: Boolean,
      default: false
    },
    canAssignMaintenance: {
      type: Boolean,
      default: false
    },
    canManageOffice: {
      type: Boolean,
      default: false
    },
    canManagePortaria: {
      type: Boolean,
      default: false
    },
    currentMode: {
      type: String,
      default: 'construcao'
    },
    showTickets: {
      type: Boolean,
      default: true
    },
    officeTab: {
      type: String,
      default: ''
    },
    warehouseTab: {
      type: String,
      default: ''
    }
  },
  emits: [
    'messenger',
    'open-create-popup',
    'start-report',
    'dialogo',
    'permissao',
    'ocorrido',
    'overview',
    'documents',
    'projects',
    'users',
    'system',
    'filter',
    'drawings',
    'my-projects',
    'my-tasks',
    'warehouse-stock',
    'warehouse-requests',
    'warehouse-request-item',
    'heavy-machinery',
    'tickets',
    'search-change',
    'search-submit',
    'start-atividade',
    'start-pendencia',
    'maintenance',
    'maintenance-manuals',
    'maintenance-service-orders',
    'maintenance-os-consulta',
    'go-geral',
    'go-admin-mode',
    'go-warehouse',
    'go-office',
    'go-portaria',
    'office-tab',
    'warehouse-planejamento',
    'warehouse-avarias',
    'warehouse-nfe-import'
  ],
  setup(props: { currentMode: string; ticketPendingCount: number; [k: string]: unknown }, { emit }: { emit: (event: string, ...args: unknown[]) => void }) {
    const adminMode = computed(() => props.currentMode === 'admin')
    const maintenanceMode = computed(() => props.currentMode === 'manutencao')
    const warehouseMode = computed(() => props.currentMode === 'almoxarifado')
    const officeMode = computed(() => props.currentMode === 'escritorio')
    const portariaMode = computed(() => props.currentMode === 'portaria')
    const showModeMenu = ref(false)

    const modeLabelText = computed(() => {
      if (props.currentMode === 'manutencao') return 'Modo: Manutenção'
      if (props.currentMode === 'admin') return 'Modo: Administrador'
      if (props.currentMode === 'almoxarifado') return 'Modo: Almoxarifado'
      if (props.currentMode === 'escritorio') return 'Modo: Escritório'
      if (props.currentMode === 'portaria') return 'Modo: Portaria'
      return 'Modo: Construção'
    })

    const setMode = (mode: 'construcao' | 'admin' | 'manutencao' | 'almoxarifado' | 'escritorio' | 'portaria') => {
      showModeMenu.value = false
      if (mode === 'manutencao') emit('maintenance')
      else if (mode === 'admin') emit('go-admin-mode')
      else if (mode === 'almoxarifado') emit('go-warehouse')
      else if (mode === 'escritorio') emit('go-office')
      else if (mode === 'portaria') emit('go-portaria')
      else emit('go-geral')
    }

    const ticketBadgeText = computed(() =>
      props.ticketPendingCount > 9 ? '+9' : String(props.ticketPendingCount)
    )

    // Active state helper for warehouse tabs (uses ?tab= query param passed as warehouseTab prop)
    const isWarehouseTab = (tab: string) => {
      const path = props.activePath as string
      if (!path.startsWith('/almoxarifado')) return false
      if (path.length > '/almoxarifado'.length && !path.startsWith('/almoxarifado?')) return false
      const wTab = props.warehouseTab as string
      if (tab === 'estoque') return !wTab || wTab === 'estoque'
      return wTab === tab
    }

    return {
      adminMode,
      maintenanceMode,
      officeMode,
      warehouseMode,
      portariaMode,
      showModeMenu,
      modeLabelText,
      setMode,
      ticketBadgeText,
      isWarehouseTab
    }
  }
})
</script>

<style scoped lang="scss">
.sidebar {
  height: 100%;
  width: 240px;
  background: var(--system-sidebar, #0a3460);
  padding: 10px 0 0 0;
  border-right: none;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

@media (max-width: 900px) {
  .sidebar {
    width: 100%;
    max-width: 100%;
    padding-top: 16px;
  }

  /* Nav links maiores para toque */
  .nav-link {
    padding: 11px 18px 11px 30px;
    font-size: 15px;
  }

  .nav-group-link {
    padding: 11px 18px;
    font-size: 15px;
  }

  .nav-group-header {
    padding: 8px 18px;
    font-size: 11px;
  }

  .create-btn {
    margin: 0 14px 6px;
    width: calc(100% - 28px);
    padding: 10px 14px;
    font-size: 14px;
  }

  .sidebar-search {
    display: none;
  }

  .identity-block {
    padding: 16px 18px 4px;
  }

  .project-label {
    padding: 4px 18px 10px;
  }

  .mode-toggle {
    padding: 12px 14px;
    padding-bottom: max(12px, env(safe-area-inset-bottom, 12px));
    gap: 8px;
  }

  .msg-btn {
    padding: 11px 14px;
    font-size: 13px;
  }

  .mode-toggle-btn {
    padding: 10px 14px;
    font-size: 13px;
  }
}

/* ── SEARCH ── */
.sidebar-search {
  margin: 10px 10px 8px;
  flex-shrink: 0;
}

/* ── CREATE BUTTON ── */
.create-btn {
  width: calc(100% - 20px);
  margin: 0 10px 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  padding: 7px 12px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.88);
  transition: background 0.12s ease, border-color 0.12s ease;
  flex-shrink: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.16);
    border-color: rgba(255, 255, 255, 0.32);
  }
}

.create-icon {
  font-size: 16px;
  line-height: 1;
  font-weight: 400;
}

.create-list {
  background: rgba(0, 0, 0, 0.18);
  padding: 8px 10px 10px;
  margin-bottom: 4px;
  flex-shrink: 0;
}

.create-section {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--system-sidebar-muted, rgba(255, 255, 255, 0.5));
  margin: 8px 2px 4px;
  padding: 0;
}

.create-link {
  display: block;
  width: 100%;
  background: transparent;
  border: none;
  text-align: left;
  cursor: pointer;
  padding: 5px 6px;
  font-size: 13px;
  color: var(--system-sidebar-text, rgba(255, 255, 255, 0.88));
  border-radius: 3px;
  transition: background 0.1s ease;
}

.create-link:hover {
  background: var(--system-sidebar-hover, rgba(255, 255, 255, 0.07));
}

/* ── IDENTITY ── */
.identity-block {
  padding: 16px 14px 4px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--system-sidebar-muted, rgba(255, 255, 255, 0.5));
  flex-shrink: 0;
}

.user-label {
  padding: 0 14px 6px;
  font-size: 12px;
  font-weight: 600;
  color: var(--system-sidebar-text, rgba(255, 255, 255, 0.88));
  flex-shrink: 0;
}

/* ── PROJECT LABEL ── */
.project-label {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 14px 10px;
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
  overflow: hidden;
  flex-shrink: 0;
}

.project-icon {
  flex-shrink: 0;
  color: rgba(255, 255, 255, 0.6);
}

.project-label-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── PANEL TITLE ── */
.panel-title-label {
  padding: 4px 14px 12px;
  font-size: 17px;
  font-weight: 700;
  color: #ffffff;
  line-height: 1.2;
  letter-spacing: -0.01em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 0;
}

/* ── NAV GROUPS ── */
.nav-group {
  margin-bottom: 2px;
  flex-shrink: 0;
}

.nav-group-header {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 14px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--system-sidebar-muted, rgba(255, 255, 255, 0.5));
  background: transparent;
  border: none;
  text-align: left;
}

.nav-group-link {
  font-size: 13px;
  font-weight: 500;
  text-transform: none;
  letter-spacing: normal;
  color: var(--system-sidebar-text, rgba(255, 255, 255, 0.88));
  padding: 7px 14px;
}

.nav-group-link:hover,
.nav-group-link.active {
  background: var(--system-sidebar-active, rgba(255, 255, 255, 0.12));
  color: #ffffff;
}

/* ── NAV LINKS ── */
.nav-links {
  display: flex;
  flex-direction: column;
  padding: 2px 0;
}

.nav-link {
  background: transparent;
  border: none;
  text-align: left;
  cursor: pointer;
  padding: 7px 14px 7px 22px;
  font-size: 13px;
  font-weight: 400;
  color: var(--system-sidebar-text, rgba(255, 255, 255, 0.88));
  transition: background 0.1s ease, color 0.1s ease;
  border-radius: 0;
  position: relative;
}

.nav-link::before {
  content: '';
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: var(--system-sidebar-muted, rgba(255, 255, 255, 0.4));
}

.nav-link:hover {
  background: var(--system-sidebar-hover, rgba(255, 255, 255, 0.07));
  color: #ffffff;
}

.nav-link.active {
  background: var(--system-sidebar-active, rgba(255, 255, 255, 0.12));
  color: #ffffff;
  font-weight: 600;
}

.nav-link.active::before {
  background: #ffffff;
}

/* ── BADGE BETA (Manuais) ── */
.nav-beta-badge {
  margin-left: 6px;
  display: inline-flex;
  align-items: center;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 1px 6px;
  border-radius: 999px;
  background: rgba(167, 139, 250, 0.25);
  color: #c4b5fd;
  border: 1px solid rgba(167, 139, 250, 0.45);
}

/* ── TICKET BADGE ── */
.ticket-badge {
  margin-left: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
  background: #d93025;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  padding: 0 5px;
}

/* ── SIDEBAR USER NAME ── */
.sidebar-user-name {
  padding: 0 4px 4px;
  font-size: 12px;
  font-weight: 600;
  color: var(--system-sidebar-text, rgba(255, 255, 255, 0.88));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── NAV SCROLL AREA ── */
.nav-scroll {
  flex: 1;
  overflow-x: hidden;
  overflow-y: auto;
  min-height: 0;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.12) transparent;
}

.nav-scroll::-webkit-scrollbar {
  width: 4px;
}

.nav-scroll::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.15);
  border-radius: 2px;
}

/* ── MODE TOGGLE ── */
.mode-toggle {
  flex-shrink: 0;
  padding: 8px 10px;
  background: rgba(0, 0, 0, 0.2);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.msg-btn {
  width: 100%;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  color: var(--system-sidebar-text, rgba(255, 255, 255, 0.88));
  transition: background 0.12s ease;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover { background: rgba(255, 255, 255, 0.12); }
}

.msg-badge {
  margin-left: auto;
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
  background: #dc2626;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
}

/* ── MODE MENU BACKDROP ── */
.mode-menu-backdrop {
  position: fixed;
  inset: 0;
  z-index: 199;
}

/* ── MODE MENU POPUP ── */
.mode-menu {
  position: relative;
  z-index: 200;
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 6px;
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.mode-menu-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: rgba(255, 255, 255, 0.4);
  padding: 2px 6px 4px;
}

.mode-option {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.75);
  text-align: left;
  transition: background 0.1s, color 0.1s, border-color 0.1s;

  &:hover {
    background: rgba(255, 255, 255, 0.09);
    color: #fff;
  }

  &.active {
    background: rgba(255, 255, 255, 0.12);
    border-color: rgba(255, 255, 255, 0.22);
    color: #fff;
    font-weight: 600;
  }

  &--maint {
    color: rgba(251, 191, 36, 0.85);
    &:hover { background: rgba(251, 191, 36, 0.1); color: rgba(251, 191, 36, 1); }
    &.active {
      background: rgba(251, 191, 36, 0.15);
      border-color: rgba(251, 191, 36, 0.35);
      color: rgba(251, 191, 36, 1);
    }
  }

  &--warehouse {
    color: rgba(52, 211, 153, 0.85);
    &:hover { background: rgba(52, 211, 153, 0.1); color: rgba(52, 211, 153, 1); }
    &.active {
      background: rgba(52, 211, 153, 0.15);
      border-color: rgba(52, 211, 153, 0.35);
      color: rgba(52, 211, 153, 1);
    }
  }

  &--office {
    color: rgba(251, 146, 60, 0.85);
    &:hover { background: rgba(251, 146, 60, 0.1); color: rgba(251, 146, 60, 1); }
    &.active {
      background: rgba(251, 146, 60, 0.15);
      border-color: rgba(251, 146, 60, 0.35);
      color: rgba(251, 146, 60, 1);
    }
  }

  &--admin {
    color: rgba(96, 165, 250, 0.85);
    &:hover { background: rgba(96, 165, 250, 0.1); color: rgba(96, 165, 250, 1); }
    &.active {
      background: rgba(96, 165, 250, 0.15);
      border-color: rgba(96, 165, 250, 0.35);
      color: rgba(96, 165, 250, 1);
    }
  }
}

.mode-check {
  margin-left: auto;
  flex-shrink: 0;
  opacity: 0.85;
}

/* ── CHANGE SYSTEM BUTTON ── */
.change-system-btn {
  width: 100%;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.75);
  display: flex;
  align-items: center;
  gap: 7px;
  text-align: left;
  transition: background 0.12s, border-color 0.12s, color 0.12s;

  &:hover {
    background: rgba(255, 255, 255, 0.13);
    border-color: rgba(255, 255, 255, 0.26);
    color: #fff;
  }

  &--open {
    background: rgba(255, 255, 255, 0.13);
    border-color: rgba(255, 255, 255, 0.28);
    color: #fff;
    .chevron-icon { transform: rotate(180deg); }
  }

  &--maint {
    color: rgba(251, 191, 36, 0.9);
    border-color: rgba(251, 191, 36, 0.3);
    background: rgba(251, 191, 36, 0.09);
    &:hover { background: rgba(251, 191, 36, 0.16); border-color: rgba(251, 191, 36, 0.45); color: rgba(251, 191, 36, 1); }
  }

  &--warehouse {
    color: rgba(52, 211, 153, 0.9);
    border-color: rgba(52, 211, 153, 0.3);
    background: rgba(52, 211, 153, 0.09);
    &:hover { background: rgba(52, 211, 153, 0.16); border-color: rgba(52, 211, 153, 0.45); color: rgba(52, 211, 153, 1); }
  }

  &--office {
    color: rgba(251, 146, 60, 0.9);
    border-color: rgba(251, 146, 60, 0.3);
    background: rgba(251, 146, 60, 0.09);
    &:hover { background: rgba(251, 146, 60, 0.16); border-color: rgba(251, 146, 60, 0.45); color: rgba(251, 146, 60, 1); }
  }

  &--admin {
    color: rgba(96, 165, 250, 0.9);
    border-color: rgba(96, 165, 250, 0.3);
    background: rgba(96, 165, 250, 0.09);
    &:hover { background: rgba(96, 165, 250, 0.16); border-color: rgba(96, 165, 250, 0.45); color: rgba(96, 165, 250, 1); }
  }
}

.chevron-icon {
  transition: transform 0.15s ease;
}

.admin-hub {
  display: flex;
  flex-direction: column;
}

.nav-group-header--mode {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 10px;
  border-top: 1px solid rgba(255,255,255,.07);
  margin-top: 2px;
  color: rgba(255, 255, 255, 0.38);
}

.create-link-nav {
  display: flex;
  align-items: center;
  gap: 7px;
  background: transparent;
  border: none;
  text-align: left;
  cursor: pointer;
  padding: 7px 14px 7px 22px;
  font-size: 12px;
  font-weight: 400;
  color: var(--system-sidebar-text, rgba(255, 255, 255, 0.7));
  width: 100%;
  border-radius: 0;
  transition: background 0.1s ease, color 0.1s ease;
  &:hover { background: var(--system-sidebar-hover, rgba(255, 255, 255, 0.07)); color: #fff; }
  svg { opacity: .6; flex-shrink: 0; }
}

.create-btn--maintenance {
  background: rgba(251, 191, 36, 0.15);
  border: 1px solid rgba(251, 191, 36, 0.35);
  color: rgba(251, 191, 36, 0.95);

  &:hover {
    background: rgba(251, 191, 36, 0.25);
    border-color: rgba(251, 191, 36, 0.55);
  }
}

.create-btn--warehouse {
  background: rgba(52, 211, 153, 0.15);
  border: 1px solid rgba(52, 211, 153, 0.35);
  color: rgba(52, 211, 153, 0.95);

  &:hover {
    background: rgba(52, 211, 153, 0.25);
    border-color: rgba(52, 211, 153, 0.55);
  }
}

.create-btn--office {
  background: rgba(251, 146, 60, 0.15);
  border: 1px solid rgba(251, 146, 60, 0.35);
  color: rgba(251, 146, 60, 0.95);

  &:hover {
    background: rgba(251, 146, 60, 0.25);
    border-color: rgba(251, 146, 60, 0.55);
  }
}

.maint-identity-block {
  display: flex;
  align-items: center;
  gap: 7px;
  color: rgba(251, 191, 36, 0.85);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .04em;
  text-transform: uppercase;
}

.warehouse-identity-block {
  display: flex;
  align-items: center;
  gap: 7px;
  color: rgba(52, 211, 153, 0.85);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .04em;
  text-transform: uppercase;
}

.office-identity-block {
  display: flex;
  align-items: center;
  gap: 7px;
  color: rgba(251, 146, 60, 0.85);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .04em;
  text-transform: uppercase;
}

.office-placeholder {
  display: block;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.45);
  padding: 8px 12px;
  font-style: italic;
  line-height: 1.5;
}

.current-mode-label {
  font-size: 10px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.35);
  text-align: center;
  padding: 2px 0 0;
  letter-spacing: 0.02em;
}

.analyst-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: rgba(250, 204, 21, 0.12);
  border: 1px solid rgba(250, 204, 21, 0.25);
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  color: rgba(250, 204, 21, 0.9);
  text-align: center;
  justify-content: center;
}

.sidebar-footer {
  margin-top: 4px;
  padding-top: 6px;
}
</style>
